import { call, put, takeLatest } from "redux-saga/effects";

import { PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import api from "service/apiService";
import { ApiResponse } from "types";
import { translate } from "utils";
import { AffiliateTransaction } from "../types";
import {
	getAffiliateTransactionsAsync,
	getAffiliateTransactionDetailsAsync,
	GetAffiliateTransactionsAsyncPayload,
	GetAffiliateTransactionDetailsPayload,
	setAffiliateTransactions,
	setAffiliateTransaction,
	verifyAffiliateTransactionAsync,
	VerifyAffiliateTransactionPayload,
	GetDetailPurchaseAsyncPayload,
	setPurchase,
	getDetailPurchaseAsync,
} from "./transactions.slice";
import { AFFILIATE_TRANSACTION_STATUS } from "constants/constants";

// Get list affiliate transactions
function* getAffiliateTransactionsSaga(action: PayloadAction<GetAffiliateTransactionsAsyncPayload>) {
	const { query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<AffiliateTransaction[]>>(
				"/affiliates/dashboard/transactions",
				{ ...query, skip: query.skip },
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		yield put({ type: setAffiliateTransactions.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetAffiliateTransactions() {
	yield takeLatest(getAffiliateTransactionsAsync, getAffiliateTransactionsSaga);
}

// Get affiliate transaction details
function* getAffiliateTransactionDetailsSaga(action: PayloadAction<GetAffiliateTransactionDetailsPayload>) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<AffiliateTransaction>>(
				`/affiliates/dashboard/transactions/${id}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setAffiliateTransaction.type, payload: response });
		callback?.(response.data);
	} else {
		message.error(translate(error.message));
	}
}

function* watchAffiliateTransactionDetails() {
	yield takeLatest(getAffiliateTransactionDetailsAsync, getAffiliateTransactionDetailsSaga);
}

// Verify affiliate transaction details
function* verifyAffiliateTransactionSaga(action: PayloadAction<VerifyAffiliateTransactionPayload>) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.patch(
				`/affiliates/dashboard/transactions/${id}`,
				{
					"status": AFFILIATE_TRANSACTION_STATUS.VERIFIED.key
				}
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(translate("Verification successful!"));
		callback?.();
	} else {
		message.error(translate(error.message));
	}
}

function* watchVerifyAffiliateTransaction() {
	yield takeLatest(verifyAffiliateTransactionAsync, verifyAffiliateTransactionSaga);
}

// Get purchase details
function* getDetailPurchaseSaga(
	action: PayloadAction<GetDetailPurchaseAsyncPayload>,
) {
	const { id, query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get(
				`/dashboard/purchase-orders/${id}`,
				query,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setPurchase.type, payload: response });
		callback?.();
	} else {
		message.error(translate(error.message));
		callback?.();
	}
}
function* watchGetDetailPurchase() {
	yield takeLatest(getDetailPurchaseAsync, getDetailPurchaseSaga);
}

const affiliateTransactionSaga = [
	watchGetAffiliateTransactions(),
	watchAffiliateTransactionDetails(),
	watchVerifyAffiliateTransaction(),
	watchGetDetailPurchase(),
];
export default affiliateTransactionSaga;
